import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    TextField,
    Grid,
} from '@mui/material';
import {
    Save as SaveIcon,
    Cancel as CancelIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import { salesOrdersAPI } from '../../../api/salesOrders';
import SOItemsManager from '../../../components/SalesOrders/SOItemsManager';
import StatusHistoryTimeline from '../../../components/SalesOrders/StatusHistoryTimeline';
import ModificationHistory from '../../../components/SalesOrders/ModificationHistory';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const SalesOrderEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [so, setSO] = useState<any>(null);
    const [originalSO, setOriginalSO] = useState<any>(null);
    const [editMode, setEditMode] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    useEffect(() => {
        if (id) {
            loadSO();
        }
    }, [id]);

    const loadSO = async () => {
        try {
            setLoading(true);
            const data = await salesOrdersAPI.getOrder(id!);
            setSO(data);
            setOriginalSO(JSON.parse(JSON.stringify(data))); // Deep copy
        } catch (error: any) {
            console.error('Failed to load SO:', error);
            enqueueSnackbar('Failed to load sales order', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const hasChanges = () => {
        if (!so || !originalSO) return false;
        return JSON.stringify(so) !== JSON.stringify(originalSO);
    };

    const handleSave = async () => {
        if (!so) return;

        // Check if SO can be edited
        if (['exported_to_zoho', 'completed'].includes(so.status)) {
            enqueueSnackbar('Cannot edit SO with status: ' + so.status, { variant: 'error' });
            return;
        }

        try {
            setSaving(true);

            const updateData = {
                delivery_date: so.delivery_date,
                order_date: so.order_date,
                notes: so.notes,
                items: so.items.map((item: any) => ({
                    item_id: item.item_id,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    notes: item.notes,
                })),
            };

            await salesOrdersAPI.updateOrder(id!, updateData);

            enqueueSnackbar('Sales order updated successfully', { variant: 'success' });
            setEditMode(false);
            loadSO(); // Reload to get updated data
        } catch (error: any) {
            console.error('Failed to update SO:', error);
            enqueueSnackbar(error.response?.data?.detail || 'Failed to update sales order', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setSO(JSON.parse(JSON.stringify(originalSO)));
        setEditMode(false);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!so) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Sales order not found</Alert>
            </Box>
        );
    }

    const canEdit = !['exported_to_zoho', 'completed', 'cancelled'].includes(so.status);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    {editMode ? 'Edit' : 'View'} SO #{so.so_number}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    {!editMode && canEdit && (
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={() => setEditMode(true)}
                        >
                            Edit
                        </Button>
                    )}

                    {editMode && (
                        <>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSave}
                                disabled={!hasChanges() || saving}
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={handleCancel}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                        </>
                    )}

                    <Button
                        variant="outlined"
                        onClick={() => navigate('/outward/sales-orders')}
                    >
                        Back to List
                    </Button>
                </Box>
            </Box>

            {/* Unsaved Changes Warning */}
            {editMode && hasChanges() && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You have unsaved changes
                </Alert>
            )}

            {/* Status Badge */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Typography variant="body1" fontWeight="medium">{so.status}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Typography variant="caption" color="text.secondary">Customer</Typography>
                        <Typography variant="body1">{so.customer_name}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Typography variant="caption" color="text.secondary">Order Date</Typography>
                        <Typography variant="body1">
                            {so.order_date ? new Date(so.order_date).toLocaleDateString('en-IN') : '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                        <Typography variant="body1" fontWeight="medium">₹{Number(so.total_amount || 0).toFixed(2)}</Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Tabs */}
            <Paper>
                <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                    <Tab label="Details" />
                    <Tab label="Status History" />
                    <Tab label="Modification History" />
                </Tabs>

                {/* Details Tab */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3}>
                        {/* Delivery Date */}
                        <Grid item xs={12} md={6}>
                            <DatePicker
                                label="Delivery Date"
                                value={so.delivery_date ? dayjs(so.delivery_date) : null}
                                onChange={(newValue: Dayjs | null) => {
                                    if (newValue) {
                                        setSO({ ...so, delivery_date: newValue.format('YYYY-MM-DD') });
                                    }
                                }}
                                format="DD/MM/YYYY"
                                disabled={!editMode}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </Grid>

                        {/* Notes */}
                        <Grid item xs={12}>
                            <TextField
                                label="Notes"
                                value={so.notes || ''}
                                onChange={(e) => setSO({ ...so, notes: e.target.value })}
                                disabled={!editMode}
                                fullWidth
                                multiline
                                rows={3}
                            />
                        </Grid>

                        {/* Items */}
                        <Grid item xs={12}>
                            <SOItemsManager
                                items={so.items || []}
                                onChange={(items) => setSO({ ...so, items })}
                                customerId={so.customer_id}
                                orderDate={so.order_date}
                                editMode={editMode}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Status History Tab */}
                <TabPanel value={tabValue} index={1}>
                    <StatusHistoryTimeline statusHistory={so.status_history || []} />
                </TabPanel>

                {/* Modification History Tab */}
                <TabPanel value={tabValue} index={2}>
                    <ModificationHistory soId={parseInt(id!)} />
                </TabPanel>
            </Paper>
        </Box>
    );
};

export default SalesOrderEdit;
